import { useState } from "react";

import Button from "../../../components/Button";
import { submitAdditionalImpacts } from "../api/questionnaireRequest";

const AdditionalImpacts = ({ severityScoreId }) => {
    const [impacts, setImpacts] = useState({});
    const [notes, setNotes] = useState("");

    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (part, option) => {
        setImpacts((prev) => {
            const next = { ...prev };

            if (option) {
                const arr = next[part] || [];
                if (arr.includes(option)) {
                    const filtered = arr.filter((o) => o !== option);
                    if (filtered.length) next[part] = filtered;
                    else delete next[part];
                } else {
                    next[part] = [...arr, option];
                }
            } else {
                if (next[part]) delete next[part];
                else next[part] = true;
            }

            return next;
        });
    };

    // handles one selection questions
    const handleSingleSelect = (field, value) => {
        setImpacts((prev) => {
            const next = { ...prev };
            // clicking the same value clears it; otherwise it replaces
            if (prev[field] === value) delete next[field];
            else next[field] = value;
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // cannot submit an empty form
        if (Object.keys(impacts).length === 0 && !notes.trim()) {
            setError(
                "Please select at least one additional impact or add some notes."
            );
            return;
        }
        setError("");
        setSubmitting(true);
        // TODO: adjust additionalimpacts location in DB

        const mappedImpacts = {
            ...impacts,
            excessive_blinking: impacts.blinking,
            wearing_equipment: impacts.equipment,
            practice_or_game: impacts.practiceOrGame,
            head_injury_location: impacts.headInjuryLocation,
        };
        delete mappedImpacts.blinking;
        delete mappedImpacts.equipment;
        delete mappedImpacts.practiceOrGame;
        delete mappedImpacts.headInjuryLocation;

        try {
            await submitAdditionalImpacts({
                severityScoreId,
                impacts: mappedImpacts,
                notes,
            });
            setSubmitted(true);
        } catch (err) {
            console.error("Submit additional impacts failed", err);
            setError("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <p className="text-teal-600 text-center">
                Your additional impacts have been saved.
            </p>
        );
    }

    return (
        // options are not mapped as a certain format is desired (ie certain fields all being on the same row, or on separate rows)
        <div>
            <h6>Additional Impacts</h6>
            <p className="text-gray-600 mb-2">
                Include any additional impacts and notes, if any.
            </p>
            <form
                onSubmit={handleSubmit}
                className="space-y-4 border border-primary p-4 rounded-lg"
            >
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Eyes */}
                    <div>
                        <h6 className="font-semibold">Eyes</h6>
                        <div className="flex gap-4">
                            {["Left", "Right"].map((opt) => (
                                <label
                                    key={opt}
                                    className="inline-flex items-center"
                                >
                                    <input
                                        type="checkbox"
                                        checked={
                                            Array.isArray(impacts.eyes) &&
                                            impacts.eyes.includes(opt)
                                        }
                                        onChange={() =>
                                            handleChange("eyes", opt)
                                        }
                                        className="mr-1 accent-indigo-600"
                                    />
                                    <span className="text-sm">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Excessive Blinking */}
                    <div>
                        <h6 className="font-semibold">Excessive Blinking</h6>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={!!impacts.blinking}
                                onChange={() => handleChange("blinking")}
                                className="mr-1 accent-indigo-600"
                            />
                            <span className="text-sm">Yes</span>
                        </label>
                    </div>

                    {/* Nose */}
                    <div>
                        <h6 className="font-semibold">Nose</h6>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={!!impacts.nose}
                                onChange={() => handleChange("nose")}
                                className="mr-1 accent-indigo-600"
                            />
                            <span className="text-sm">Yes</span>
                        </label>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Jaw */}
                    <div>
                        <h6 className="font-semibold">Jaw</h6>
                        <div className="flex gap-4">
                            {["Left", "Right"].map((opt) => (
                                <label
                                    key={opt}
                                    className="inline-flex items-center"
                                >
                                    <input
                                        type="checkbox"
                                        checked={
                                            Array.isArray(impacts.jaw) &&
                                            impacts.jaw.includes(opt)
                                        }
                                        onChange={() =>
                                            handleChange("jaw", opt)
                                        }
                                        className="mr-1 accent-indigo-600"
                                    />
                                    <span className="text-sm">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Chin */}
                    <div>
                        <h6 className="font-semibold">Chin</h6>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={!!impacts.chin}
                                onChange={() => handleChange("chin")}
                                className="mr-1 accent-indigo-600"
                            />
                            <span className="text-sm">Yes</span>
                        </label>
                    </div>

                    {/* Throat */}
                    <div>
                        <h6 className="font-semibold">Throat</h6>
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={!!impacts.throat}
                                onChange={() => handleChange("throat")}
                                className="mr-1 accent-indigo-600"
                            />
                            <span className="text-sm">Yes</span>
                        </label>
                    </div>
                </div>

                {/* Chest */}
                <div>
                    <h6 className="font-semibold">Chest</h6>
                    <div className="flex gap-4">
                        {["Left", "Center", "Right"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.chest) &&
                                        impacts.chest.includes(opt)
                                    }
                                    onChange={() => handleChange("chest", opt)}
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Shoulder */}
                <div>
                    <h6 className="font-semibold">Shoulder</h6>
                    {[
                        { position: "Front", sides: ["Left", "Right"] },
                        { position: "Back", sides: ["Left", "Right"] },
                    ].map(({ position, sides }) => (
                        <div
                            key={position}
                            className="flex items-center gap-2 mb-1"
                        >
                            <span className="font-medium mr-1">
                                {position}:
                            </span>
                            <div className="flex gap-4">
                                {sides.map((side) => {
                                    const optionValue = `${position} ${side}`;
                                    return (
                                        <label
                                            key={optionValue}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(
                                                        impacts.shoulder
                                                    ) &&
                                                    impacts.shoulder.includes(
                                                        optionValue
                                                    )
                                                }
                                                onChange={() =>
                                                    handleChange(
                                                        "shoulder",
                                                        optionValue
                                                    )
                                                }
                                                className="mr-1 accent-indigo-600"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">
                                                {side}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Upper Back */}
                <div>
                    <h6 className="font-semibold">Upper Back</h6>
                    <div className="flex gap-4">
                        {["Left", "Center", "Right"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.upper_back) &&
                                        impacts.upper_back.includes(opt)
                                    }
                                    onChange={() =>
                                        handleChange("upper_back", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Mid Back */}
                <div>
                    <h6 className="font-semibold">Mid Back</h6>
                    <div className="flex gap-4">
                        {["Left", "Center", "Right"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.mid_back) &&
                                        impacts.mid_back.includes(opt)
                                    }
                                    onChange={() =>
                                        handleChange("mid_back", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Lower Back */}
                <div>
                    <h6 className="font-semibold">Lower Back</h6>
                    <div className="flex gap-4">
                        {["Left", "Sacrum", "Right"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.lower_back) &&
                                        impacts.lower_back.includes(opt)
                                    }
                                    onChange={() =>
                                        handleChange("lower_back", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Hip */}
                <div>
                    <h6 className="font-semibold">Hip</h6>
                    {[
                        { position: "Front", sides: ["Left", "Right"] },
                        { position: "Back", sides: ["Left", "Right"] },
                    ].map(({ position, sides }) => (
                        <div
                            key={position}
                            className="flex items-center gap-2 mb-1"
                        >
                            <span className="font-medium mr-1">
                                {position}:
                            </span>
                            <div className="flex gap-4">
                                {sides.map((side) => {
                                    const optionValue = `${position} ${side}`;
                                    return (
                                        <label
                                            key={optionValue}
                                            className="inline-flex items-center"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={
                                                    Array.isArray(
                                                        impacts.hip
                                                    ) &&
                                                    impacts.hip.includes(
                                                        optionValue
                                                    )
                                                }
                                                onChange={() =>
                                                    handleChange(
                                                        "hip",
                                                        optionValue
                                                    )
                                                }
                                                className="mr-1 accent-indigo-600"
                                                disabled={submitting}
                                            />
                                            <span className="text-sm">
                                                {side}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Knee */}
                <div>
                    <h6 className="font-semibold">Knee</h6>
                    <div className="flex gap-4">
                        {["Left", "Right"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.knee) &&
                                        impacts.knee.includes(opt)
                                    }
                                    onChange={() => handleChange("knee", opt)}
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Equipment */}
                <div>
                    <h6 className="font-semibold">Wearing Equipment</h6>
                    <div className="flex gap-4">
                        {["Yes", "No"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={impacts.equipment === opt}
                                    onChange={() =>
                                        handleSingleSelect("equipment", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Practice/Game */}
                <div>
                    <h6 className="font-semibold">Practice or Game</h6>
                    <div className="flex gap-4">
                        {["Practice", "Game"].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={impacts.practiceOrGame === opt}
                                    onChange={() =>
                                        handleSingleSelect(
                                            "practiceOrGame",
                                            opt
                                        )
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Head Injury Location */}
                <div>
                    <h6 className="font-semibold">Head Injury Location</h6>
                    <div className="flex flex-col gap-2 md:flex-wrap md:flex-row">
                        {[
                            "Front of Head",
                            "Back of Head",
                            "Left Side of Head",
                            "Top of Head",
                            "Right Side of Head",
                            "Neck",
                        ].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(
                                            impacts.headInjuryLocation
                                        ) &&
                                        impacts.headInjuryLocation.includes(opt)
                                    }
                                    onChange={() =>
                                        handleChange("headInjuryLocation", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Symptoms */}
                <div>
                    <h6 className="font-semibold">Symptoms</h6>
                    <div className="flex flex-col gap-2 md:flex-wrap md:flex-row">
                        {[
                            "Loss of Consciousness",
                            "Disorientation",
                            "Incoherent Speech",
                            "Confusion",
                            "Memory Loss",
                            "Dazed or Vacant Stare",
                        ].map((opt) => (
                            <label
                                key={opt}
                                className="inline-flex items-center"
                            >
                                <input
                                    type="checkbox"
                                    checked={
                                        Array.isArray(impacts.symptoms) &&
                                        impacts.symptoms.includes(opt)
                                    }
                                    onChange={() =>
                                        handleChange("symptoms", opt)
                                    }
                                    className="mr-1 accent-indigo-600"
                                />
                                <span className="text-sm">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <h6 className="font-semibold">Additional Notes</h6>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Include any additional notes"
                        className="w-full border p-2 rounded"
                    />
                </div>
                {error && <p className="text-red-600">{error}</p>}
                <Button type="submit" className="w-full">
                    Save Additional Impacts
                </Button>
            </form>
        </div>
    );
};

export default AdditionalImpacts;